import debounce from 'lodash/debounce';
import { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components/macro';

import { t } from '../../config/i18n';
import { ISearchData } from '../../domain/services/GeneralServiceEntities';
import useMediaQuery from '../../hooks/useMediaQuery';
import GeneralService from '../../services/GeneralService';
import { classNames } from '../../utils/api';
import { ArrowRightIcon, Defaults, Search } from '../common';
import InputEx from '../controls/InputEx';
import { buttonHeight, mobileButtonHeight } from './TopBar';

const emprySearch = {
  deal: undefined,
  lot: undefined,
  user: undefined,
};

export const SearchComponent = ({ className }: { className?: string }) => {
  const [search, setSearch] = useState('');
  const [value, setValue] = useState<ISearchData>(emprySearch);
  const is1024 = useMediaQuery('(max-width: 1024px)');

  const debouncedSearch = useCallback(
    debounce((params) => GeneralService.search(params).then(setValue), 500, {
      trailing: true,
    }),
    [],
  );

  const valueIsFound = !!value.deal || !!value.lot || !!value.user;

  useEffect(() => {
    setValue(emprySearch);
    if (search.length >= 5 && search.length <= 12) debouncedSearch({ search });
  }, [search]);

  return (
    <SearchComponentContainer
      className={classNames('search-component-container', className)}>
      <span className={'hint'}>{t('top-bar.search-enter-id')}</span>
      <InputEx
        borderColor={'transparent'}
        id={'search-input'}
        width={'100%'}
        hint={t('top-bar.search-placeholder')}
        value={search}
        height={is1024 ? mobileButtonHeight : buttonHeight}
        autoFocus
        onChange={setSearch}
        type={'text'}
      />
      <Search />
      {valueIsFound && (
        <div className="search-results-container">
          {!!value.deal && <SearchResult type="deal" value={value} />}
          {!!value.lot && <SearchResult type="lot" value={value} />}
          {!!value.user && <SearchResult type="user" value={value} />}
        </div>
      )}
    </SearchComponentContainer>
  );
};

const SearchResult = ({
  type,
  value,
}: {
  type: 'deal' | 'user' | 'lot';
  value: ISearchData;
}) => {
  const url = useMemo(() => {
    if (type === 'lot') {
      return value.lot?.is_owner
        ? `/bids/${value.lot?.id}`
        : `/deals/bid$${value.lot?.id}`;
    }
    return `/${type}s/${value[type]}`;
  }, [value, type]);

  return (
    <a className="search-result" href={url}>
      <span className={'search-result-hint'}>{t(`top-bar.search-${type}`)}</span>
      <a className="search-result-link">{type === 'lot' ? value.lot?.id : value[type]}</a>
      <ArrowRightIcon />
    </a>
  );
};

const SearchComponentContainer = styled.div`
  background-color: ${Defaults.inputsBgColor};
`;
